import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import {
    Box,
    Card,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    useTheme,
    ListItemAvatar,
    Tooltip
} from "@mui/material";
import { TabPanel, TabContext } from "@mui/lab";

import DashboardIcon from "../icons/DashboardIcon";
import StoresIcon from "../icons/StoresIcon";
import UsersIcon from "../icons/UsersIcon";
import SettingsIcon from "../icons/SettingsIcon";
import InventoryScanIcon from "../icons/InventoryScanIcon";

import apiClient from "../../services/api";
import store from "../../store";
import { toggleMessageOpen } from "../../store/actions/Message";

import defaultProjectImage from '../../img/default.png';

//import { checkIfModuleEnabled } from "../../utils/Utils";

import StyledTab from "../styled/Tab";
import StyledTabs from "../styled/Tabs";

const getModuleStatus = (modules, moduleName) => {
    let isEnabled = false;

    if(!modules || modules.length === 0) {
        return false;
    }
    
    modules.forEach(function (module) {
        if(module.name === moduleName) {
            isEnabled = module.is_enabled;
        }
    })

    return isEnabled;
}

function StartingPage()
{
    const navigate = useNavigate();
    const theme = useTheme()
    const {t} = useTranslation()

    const [selectedTab, setSelectedTab] = useState(0);
    const [addedProjects, setAddedProjects] = useState([]);
    const [editedProjects, setEditedProjects] = useState([]);

    const [isEasyFloorEnabled, setIsEasyFloorEnabled] = useState(false);
    const [isEasyBoardOnlineEnabled, setIsEasyBoardOnlineEnabled] = useState(false);
    const [isUsersEnabled, setIsUsersEnabled] = useState(false);
    const [isInventoryScanEnabled, setIsInventoryScanEnabled] = useState(false);
    const [isSettingsEnabled, setIsSettingsEnabled] = useState(false);
    
    const { innerWidth: width, innerHeight: height } = window;

    //const modules = useSelector((state) => state.auth.modules);

    useEffect(() => {
        apiClient.post('/api/modules/list').then(response => {
            const modules = response.data;

            setIsEasyFloorEnabled(getModuleStatus(modules, 'easyFloor'))
            setIsEasyBoardOnlineEnabled(getModuleStatus(modules, 'easyBoardOnline'))
            setIsUsersEnabled(getModuleStatus(modules, 'users'))
            setIsInventoryScanEnabled(getModuleStatus(modules, 'scannedProjects'))
            setIsSettingsEnabled(getModuleStatus(modules, 'settings'))
        }).catch(error => {})
    }, []);

    useEffect(() => {
        apiClient.get('/api/projects/recent').then(response => {
            if(response.data.success) {
                setAddedProjects(response.data.added)
                setEditedProjects(response.data.edited)
            } else {
                store.dispatch(toggleMessageOpen(t("Projects can't be loaded"), 'error'))
            }
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t("Projects cant't be loaded"), "error"))
        })
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeTab = (event, newTab) => { setSelectedTab(newTab) }

    const Menu = () => {
        return(
            <Fragment>
                <Grid container spacing={5} alignItems='center' alignContent='center'>
                    <Grid item xl={2.4} lg={2.4} md={4} sm={6} xs={12}>
                        <MenuItem
                            item={{
                                title: t('Floor Plans'),
                                href: '/app/easy-floor',
                                color: '#C692FA',
                                icon: StoresIcon,
                                enabled: isEasyFloorEnabled
                            }}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={4} sm={6} xs={12}>
                        <MenuItem
                            item={{
                                title: t('Planograms'),
                                href: '/app/project',
                                color: '#F2A289',
                                icon: DashboardIcon,
                                enabled: isEasyBoardOnlineEnabled
                            }}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={4} sm={6} xs={12}>
                        <MenuItem
                            item={{
                                title: t('Automatic Planograms'),
                                href: '/app/scanned-project',
                                color: '#7E93FF',
                                icon: InventoryScanIcon,
                                enabled: isInventoryScanEnabled
                            }}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={4} sm={6} xs={12}>
                        <MenuItem
                            item={{
                                title: t('Users'),
                                href: '/app/users',
                                color: '#F0C078',
                                icon: UsersIcon,
                                enabled: isUsersEnabled
                            }}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={4} sm={6} xs={12}>
                        <MenuItem
                            item={{
                                title: t('Settings'),
                                href: '/app/settings',
                                color: '#40B2E0',
                                icon: SettingsIcon,
                                enabled: isSettingsEnabled
                            }}
                        />
                    </Grid>
                </Grid>
            </Fragment>
        )
    }

    const MenuItem = ({item}) => {
        return (
            <Tooltip title={item.title} placement="left-start">
                <Card
                    component={item.enabled ? Link : Card}
                    to={item.href}
                    style={{
                        backgroundColor: item.enabled ? theme.palette.background.secondary : '#CECECE',
                        opacity: item.enabled ? 1 : 0.6,
                        padding: '5px',
                        height: '208px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        cursor: item.enabled ? 'pointer' : 'default',
                        margin: 'auto',
                        textDecoration: 'none'
                    }}
                >
                    <div
                        style={{
                            width: '56px',
                            height: '56px',
                            backgroundColor: item.color,
                            borderRadius: '12px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <item.icon color='#FFF' />
                    </div>
                    <div style={{marginTop: '10px'}}>{item.title}</div>
                </Card>
            </Tooltip>
        )
    }

    const LatestItems = () => {
        if(! isEasyBoardOnlineEnabled) {
            return null;
        }

        return (
            <div
                style={{
                    backgroundColor: theme.palette.background.secondary,
                    marginTop: '25px',
                    borderRadius: '12px',
                    height: 'calc(100vh - 280px)',
                    overflow: 'hidden'
                }}
            >
                <Typography variant="h1" style={{padding: '24px', fontSize: window.innerWidth > 650 ? '2em' : '1em'}}>
                    {t('Recent projects')}
                </Typography>
                
                <TabContext value={selectedTab.toString()}>
                    <Box
                        style={{
                            backgroundColor: theme.palette.storeDrawer.tabs.backgroundColor,
                            borderRadius: '8px',
                            height: '50px',
                            width: width >= 500 ? '350px' : 'calc(100% - 10px)',
                            marginLeft: width >= 500 ? '24px' : '5px'
                        }}
                    >
                        <StyledTabs
                            value={selectedTab}
                            onChange={handleChangeTab}
                            textColor="inherit"
                            variant={width >= 500 ? "normal" : "fullWidth"}
                        >
                            <StyledTab label={t('Recently added')} />
                            <StyledTab label={t('Recently edited')} />
                        </StyledTabs>
                    </Box>
                    
                    <TabPanel value="0" style={{padding: 0}}>
                        <ProjectList type='added' />
                    </TabPanel>
                    
                    <TabPanel value="1" style={{padding: 0}}>
                        <ProjectList type='edited' />
                    </TabPanel>
                </TabContext>
            </div>
        )
    }

    const ProjectList = ({type}) => {
        if(!addedProjects && !editedProjects) {
            return;
        }

        const list = type === 'added' ? addedProjects : editedProjects

        return (
            <List style={{height: 'calc(100vh - 392px)', overflowY: 'auto'}} >
                {list && list.length > 0 ? (
                    list.map((project) => (
                        <ProjectListItem key={project.id} item={project} listType={type} />
                    ))
                ) : (
                    <Typography style={{padding: '24px'}}>{t('Loading projects!')}</Typography>
                )}
            </List>
        )
    }

    const ProjectListItem = ({item, listType}) => {
        const date = listType === 'added' ? item.created_at : item.updated_at
        
        return (
            <ListItemButton
                key={item.id}
                role={undefined}
                style={{padding: '8px', paddingLeft: '24px', paddingRight: '0', borderBottom: '1px solid '+theme.palette.border.primary}}
                dense
                onClick={() => { navigate('/app/project/view/'+item.id) }}
            >
                <ListItemAvatar
                    style={{
                        width: '76px',
                        height: '56px',
                        borderRadius: '5px',
                        backgroundColor: theme.palette.background.default,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '15px'
                    }}
                >
                    <img src={item.screenshot ? item.screenshot : defaultProjectImage} alt='project' style={{maxWidth: '100%', maxHeight: '100%'}} />
                </ListItemAvatar>
                <ListItemText primary={item.name} secondary={new Date(date).toLocaleDateString() } />
            </ListItemButton>
        )
    }

    return (<>
        <Helmet>
            <title>{t("Dashboard")} - easyBoardOnline</title>
        </Helmet>
        <Box sx={{width: '80%', margin: 'auto', paddingTop: '24px', height: 'calc(100vh - 80px)'}}>
            <Menu />
            <LatestItems />
        </Box>
    </>)
}

export default StartingPage;
